import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './ParlayRole.css';
import PreviewParlayRole from './PreviewParlayRole';
import {useProfile} from '../context/ProfileContext';
function ParlayRole() {
  const navigate = useNavigate();
  const location = useLocation();
  const { match: initialMatch, amount ,type} = location.state || {};

  const [matchDetails, setMatchDetails] = useState(null);
  const [parlayOptions, setParlayOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { fetchNameWallet} = useProfile();
  useEffect(() => {
    const fetchMatchDetails = async () => {
      if (!initialMatch?._id) {
        setError('Invalid match data provided.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/match-setup/${initialMatch._id}`);
        if (!response.ok) {
          throw new Error('Failed to fetch match details');
        }
        const data = await response.json();
        if (data.success && data.data) {
          setMatchDetails(data.data);

          const fetchedRules = data.data.parlayRules || [];
          const dynamicOptions = fetchedRules.map((rule, index) => ({
            id: `parlayRule_${index}`,
            label: rule.statement,
            odds: rule.odds
          }));
          setParlayOptions(dynamicOptions);

        } else {
          throw new Error(data.message || 'Could not retrieve match data');
        }
      } catch (err) {
        setError(err.message);
        console.error('Error fetching match details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchMatchDetails();
  }, [initialMatch?._id]);

  const handleOptionSelect = (option) => {
    setSelectedOptions(prev => {
      const isSelected = prev.some(selected => selected.id === option.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== option.id);
      } else {
        return [...prev, option];
      }
    });
  };

  const calculateTotalOdds = () => {
    return selectedOptions
      .map(opt => parseFloat(opt.odds) || 1)
      .reduce((total, odds) => total * odds, 1)
      .toFixed(2);
  };

  const calculatePotentialWin = () => {
    if (!amount) return '0.00';
    const totalOdds = parseFloat(calculateTotalOdds());
    const betAmount = parseFloat(amount) || 0;
    return (betAmount * totalOdds).toFixed(2);
  };

  const canConfirm = selectedOptions.length >= 3;

  const handlePreviewParlay = () => {
    if (!canConfirm) return;
    setShowPreview(true);
  };
  // console.log(initialMatch.matchId);

  const handleEditSelections = () => {
    setShowPreview(false);
  };

  const handleConfirmParlay = async () => {
    if (!canConfirm) return;

    // Get user data from localStorage
    const userString = localStorage.getItem('user');
    
    if (!userString) {
      alert('You must be logged in to place a bet');
      navigate('/login');
      return;
    }
    
    // Parse user data from localStorage
    let userData;
    try {
      userData = JSON.parse(userString);
    } catch (error) {
      console.error('Error parsing user data:', error);
      alert('Error with user session. Please log in again.');
      navigate('/login');
      return;
    }
    
    // Get userId from user data
    const userId = userData.id || userData._id || userData.userId;
    
    if (!userId) {
      alert('User ID not found. Please log in again.');
      navigate('/login');
      return;
    }

    const parlayData = {
      userId, // Include userId in the request
      matchId: initialMatch.matchId,
      team1: matchDetails.team1,
      team2: matchDetails.team2,
      amount: amount,
      selectedOptions: selectedOptions,
      totalOdds: calculateTotalOdds(),
      potentialWin: calculatePotentialWin(),
      type: type,
      match: initialMatch.parlayRules
    };
    console.log('Sending data:', parlayData);

    try {
      // First try the test route
      console.log('Trying test route first');
      const testResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/api/test-parlay-bets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({test: true, ...parlayData}),
      });
      
      const testResult = await testResponse.json();
      console.log('Test route response:', testResult);
      
      // Try the direct route
      console.log('Trying direct route');
      const directResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/api/direct-parlay-bets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({direct: true, ...parlayData}),
      });
      
      const directResult = await directResponse.json();
      console.log('Direct route response:', directResult);
      
      // Now try the actual route
      console.log('Now trying the actual route');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/parlay-bets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(parlayData),
      });

      const result = await response.json();
      if (result.success) {
        console.log('Parlay bet saved successfully');
        setShowConfirmation(true);
        setShowPreview(false);
        fetchNameWallet();
      } else {
        console.error('Failed to save parlay bet:', result.message);
        alert(`Failed to save bet: ${result.message}`);
      }
    } catch (error) {
      console.error('Error saving parlay bet:', error);
      alert('An error occurred. Please try again. Details: ' + error.message);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="parlay-role-page loading-state">
        <div className="parlay-header">
          <button className="back-button" onClick={() => navigate(-1)}><span>←</span> Back</button>
          <h1 className="page-title">Loading Parlay...</h1>
        </div>
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !matchDetails) {
    return (
      <div className="parlay-role-page error-state">
        <div className="parlay-header">
          <button className="back-button" onClick={() => navigate(-1)}><span>←</span> Back</button>
          <h1 className="page-title">Parlay Error</h1>
        </div>
        <div className="error-message">{error || 'Could not load parlay details.'}</div>
      </div>
    );
  }

  if (showPreview) {
    return (
      <PreviewParlayRole
        matchDetails={matchDetails}
        selectedOptions={selectedOptions}
        amount={amount}
        totalOdds={calculateTotalOdds()}
        potentialWin={calculatePotentialWin()}
        onEdit={handleEditSelections}
        onConfirm={handleConfirmParlay}
        onGoBack={handleGoBack}
      />
    );
  }

  return (
    <div className="parlay-role-page">
      <div className="parlay-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <span>←</span>
        </button>
        <h1 className="page-title">Parlay Role</h1>
      </div>

      <div className="parlay-content">
        <div className="match-info">
          <div className="teams-container">
            <div className="team">
              <span className="team-name">{matchDetails.team1}</span>
            </div>
            <div className="vs-text">VS</div>
            <div className="team">
              <span className="team-name">{matchDetails.team2}</span>
            </div>
          </div>
          <div className="bet-amount">
            <span>Stake Amount:</span>
            <span className="amount">₹{amount || 'N/A'}</span>
          </div>
        </div>

        <div className="parlay-options">
          <h2 className="section-title">Select Parlay Options (Min. 3)</h2>

          <div className="options-grid">
            {parlayOptions.length === 0 ? (
              <div className="no-options-message">No parlay rules available for this match.</div>
            ) : (
              <div className="option-buttons">
                {parlayOptions.map(option => (
                  <button
                    key={option.id}
                    className={`option-button ${selectedOptions.some(selected => selected.id === option.id) ? 'selected' : ''}`}
                    onClick={() => handleOptionSelect(option)}
                  >
                    <span className="option-label">{option.label}</span>
                    <span className="option-odds">{option.odds}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedOptions.length > 0 && selectedOptions.length < 3 && (
              <div className="selection-message">
                Select {3 - selectedOptions.length} more option{3 - selectedOptions.length !== 1 ? 's' : ''} (minimum 3 total)
              </div>
            )}
          </div>
        </div>

        <div className="parlay-summary">
          <button
            className="confirm-parlay-button"
            disabled={!canConfirm}
            onClick={handlePreviewParlay}
          >
            {canConfirm ? 'Preview Parlay' : 'Select at least 3 Options'}
          </button>
        </div>
      </div>

      {showConfirmation && (
        <div className="confirmation-overlay">
          <div className="confirmation-popup">
            <div className="confirmation-icon">✓</div>
            <h2>Parlay Role Confirmed!</h2>
            <p>Your Parlay has been successfully placed.</p>
            <button className="go-back-button" onClick={handleGoBack}>
              Go Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParlayRole;
